import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSyncDuelSocket } from '../../contexts/SyncDuelSocketContext.tsx';
import {
  Box,
  Grid,
  GridItem,
  HStack,
  VStack,
  Text,
  Heading,
  Button,
  Progress,
  useBreakpointValue,
  Badge,
  IconButton,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import sfx from '../../lib/goofyAudio.ts';
import { auth } from '../../config/firebase.ts';
import PlayerName from '../Common/PlayerName.tsx';
import './SyncDuelPlay.css';

// Motion-capable Chakra wrapper
const MotionBox = motion(Box);

// Child that owns the high-frequency timer so parent/buttons don't rerender
function SmoothTimer({ small, endsAt, durationMs = 10000, onUrgencyChange, onSecond, paused }: { small: boolean; endsAt: number; durationMs?: number; onUrgencyChange: (v: boolean) => void; onSecond?: (secLeft: number) => void; paused?: boolean }) {
  const [leftMs, setLeftMs] = useState<number>(0);
  const prevUrgent = useRef<boolean>(false);
  const prevWholeRef = useRef<number>(-1);

  useEffect(() => {
    const update = () => {
  if (paused) return; // freeze while paused
  const ms = Math.max(0, (endsAt || 0) - Date.now());
  setLeftMs(ms);
      const nowUrgent = ms < 2500;
      if (nowUrgent !== prevUrgent.current) {
        prevUrgent.current = nowUrgent;
        onUrgencyChange(nowUrgent);
      }
      // fire per-second callback without rerendering parent
      if (onSecond) {
        const whole = Math.floor(ms / 1000);
        if (whole !== prevWholeRef.current) {
          prevWholeRef.current = whole;
          onSecond(whole);
        }
      }
    };
    update();
    const id = setInterval(update, 100);
    return () => clearInterval(id);
  }, [endsAt, onUrgencyChange, paused]);

  const ratio = Math.max(0, Math.min(1, leftMs / durationMs));
  const progressPct = ratio * 100;
  const hue = Math.round(120 * ratio);
  const progressColor = `hsl(${hue}, 85%, 50%)`;
  // UI: we only show the progress bar (no numeric seconds label)

  return (
    <Box minW={{ base: '240px', md: '420px' }}>
      <Progress
        w={{ base: '240px', md: '420px' }}
        value={progressPct}
        size="sm"
        bg="#222"
        sx={{
          '& > div': {
            transition: 'width 80ms linear, background-color 120ms linear',
            backgroundColor: progressColor,
          },
        }}
      />
    </Box>
  );
}

const Bar: React.FC<{ ratio: number; color: string; side: 'left'|'right'; glow?: boolean }>=({ ratio, color, side, glow })=>{
  const height = Math.min(100, Math.max(0, ratio*100));
  return (
    <Box position="absolute" top={{base:16, md:20}} bottom={{base:16, md:20}} {...{[side]: 2}} w={3} bg="#1a1a1a" borderRadius={6}>
      <MotionBox
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        style={{ height: `${height}%` }}
        bg={color}
        borderRadius={6}
        animate={{
          boxShadow: glow ? `0 0 18px ${color}AA, 0 0 36px ${color}55` : 'none',
        }}
        transition={{ duration: 0.25, ease: 'easeInOut' }}
      />
    </Box>
  );
};

// Edge bars hugging screen sides (QuizUp-like), purely decorative and non-interactive
const EdgeBars: React.FC<{
  leftRatio: number;
  rightRatio: number;
  leftColor?: string;
  rightColor?: string;
  leftGained?: boolean;
  rightGained?: boolean;
}> = ({ leftRatio, rightRatio, leftColor = '#ffcc00', rightColor = '#00d0ff', leftGained, rightGained }) => {
  const leftHeight = Math.min(100, Math.max(0, leftRatio * 100));
  const rightHeight = Math.min(100, Math.max(0, rightRatio * 100));
  return (
    <Box position="absolute" inset={0} pointerEvents="none" zIndex={1}>
      {/* Left edge bar */}
      <Box position="absolute" left={0} top={{ base: 14, md: 16 }} bottom={{ base: 14, md: 16 }} w={{ base: '10px', md: '14px' }} bg="#0e0e0e" borderRight="1px solid #1f1f1f">
        <MotionBox
          position="absolute"
          left={0}
          right={0}
          bottom={0}
          style={{ height: `${leftHeight}%` }}
          bg={`linear-gradient(180deg, ${leftColor}, ${leftColor}CC)`}
          borderRight="1px solid rgba(255,255,255,0.1)"
          borderTopLeftRadius={2}
          borderTopRightRadius={2}
          animate={{ filter: leftGained ? 'brightness(1.4) saturate(1.3)' : 'none' }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          overflow="hidden"
        >
          {/* stripes when gaining */}
          {leftGained && (
            <MotionBox
              position="absolute"
              inset={0}
              bg="repeating-linear-gradient(135deg, rgba(255,255,255,0.22) 0px, rgba(255,255,255,0.22) 4px, transparent 4px, transparent 10px)"
              initial={{ y: 20 }}
              animate={{ y: -20 }}
              transition={{ duration: 0.5, ease: 'linear' }}
            />
          )}
        </MotionBox>
      </Box>
      {/* Right edge bar */}
      <Box position="absolute" right={0} top={{ base: 14, md: 16 }} bottom={{ base: 14, md: 16 }} w={{ base: '10px', md: '14px' }} bg="#0e0e0e" borderLeft="1px solid #1f1f1f">
        <MotionBox
          position="absolute"
          left={0}
          right={0}
          bottom={0}
          style={{ height: `${rightHeight}%` }}
          bg={`linear-gradient(180deg, ${rightColor}, ${rightColor}CC)`}
          borderLeft="1px solid rgba(255,255,255,0.1)"
          borderTopLeftRadius={2}
          borderTopRightRadius={2}
          animate={{ filter: rightGained ? 'brightness(1.4) saturate(1.3)' : 'none' }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          overflow="hidden"
        >
          {rightGained && (
            <MotionBox
              position="absolute"
              inset={0}
              bg="repeating-linear-gradient(225deg, rgba(255,255,255,0.22) 0px, rgba(255,255,255,0.22) 4px, transparent 4px, transparent 10px)"
              initial={{ y: 20 }}
              animate={{ y: -20 }}
              transition={{ duration: 0.5, ease: 'linear' }}
            />
          )}
        </MotionBox>
      </Box>
    </Box>
  );
};

export default function SyncDuelPlay(){
  const { code } = useParams();
  const navigate = useNavigate();
  const { state, answer, join, connected } = useSyncDuelSocket();
  const [selected, setSelected] = useState<number | null>(null);
  // Coarse-grained urgency flag, toggles once per question when < 2.5s
  const [urgent, setUrgent] = useState<boolean>(false);
  const triedJoinRef = useRef(false);
  const [muted, setMuted] = useState<boolean>(() => { try { return sfx.isMuted(); } catch { return false; } });

  // MotionBox declared at module scope

  // Sort by numeric id to keep a stable left/right mapping on both clients
  const sortedPlayers = useMemo(() => {
    return [...(state?.players || [])].sort((a,b)=> (parseInt(a.id) || 0) - (parseInt(b.id) || 0));
  }, [state?.players]);
  const [p1, p2] = sortedPlayers;
  const prevScoresRef = useRef<{ left: number; right: number }>({ left: 0, right: 0 });
  const currScores = { left: p1?.score || 0, right: p2?.score || 0 };
  const gainedLeft = currScores.left - prevScoresRef.current.left;
  const gainedRight = currScores.right - prevScoresRef.current.right;
  useEffect(() => { prevScoresRef.current = currScores; }, [currScores.left, currScores.right]);
  const ratioLeft = (p1?.score||0) / Math.max(1, (p1?.score||0)+(p2?.score||0));
  const ratioRight = (p2?.score||0) / Math.max(1, (p1?.score||0)+(p2?.score||0));
  const small = useBreakpointValue({ base: true, md: false });
  // Memoize options array to avoid rerenders breaking hover/click during timer updates
  const options = useMemo(() => state?.question?.options || [], [state?.question?.options]);
  const cols = useBreakpointValue({ base: 1, sm: 2 });
  const isMobile = useBreakpointValue({ base: true, md: false });

  const handleAnswer = (i: number) => {
    if (!code) return;
    // avoid spamming on reveal
    if (state?.reveal) return;
    if (state?.paused) return; // don't accept answers while paused
    if (selected !== null) return;
    setSelected(i);
  try { sfx.click(); } catch {}
    answer(code, i+1);
    // Persist local lock for this question to survive F5
    try {
      const key = `syncduel:answered:${code}:${state?.index || 0}:${state?.question?.endsAt || 0}`;
      localStorage.setItem(key, String(i));
    } catch {}
  };

  // Defensive auto-join: always (re)join once so this socket is registered server-side
  useEffect(() => {
    if (!connected || !code || triedJoinRef.current) return;
    const meName = (localStorage.getItem('syncduel:name') || auth.currentUser?.displayName || '').toString().trim();
    const safeName = meName || `Joueur-${Math.floor(Math.random()*1000)}`;
  const uid = auth.currentUser?.uid || localStorage.getItem('syncduel:uid') || undefined;
    triedJoinRef.current = true;
  join(code, safeName, uid);
  }, [connected, code, join]);

  // Auto-scroll to top when arriving on the page or when a new question appears
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [state?.index]);

  // Reset selection when a new question arrives
  useEffect(() => {
    // Restore selection lock if already answered (prevents F5 re-answer)
    let restored: number | null = null;
    try {
      const key = `syncduel:answered:${code}:${state?.index || 0}:${state?.question?.endsAt || 0}`;
      const v = localStorage.getItem(key);
      if (v !== null) restored = Number(v);
    } catch {}
    setSelected(Number.isFinite(restored as any) ? (restored as number) : null);
    if (state?.question?.endsAt) {
      try { sfx.startRound(); } catch {}
    }
  }, [state?.index, state?.question?.endsAt]);

  // Drive a local high-resolution countdown so UI animates smoothly
  // Reset urgency on new question
  useEffect(() => {
    setUrgent(false);
  }, [state?.index]);

  // Prepare / unlock audio
  useEffect(() => { try { sfx.prepare(); } catch {} }, []);
  const onFirstGesture = React.useCallback(() => { try { sfx.unlock(); } catch {} }, []);

  // Reveal SFX
  useEffect(() => {
    const r = state?.reveal;
    if (!r) return;
    const players = state?.players || [];
    const me = players.find(p => (localStorage.getItem('syncduel:name') || '') === p.name) || players[0];
    if (!me) return;
    const myId = me.id;
    const iAmCorrect = (r.round?.correct?.[myId] === true) || (r.correctIndex && selected !== null && (r.correctIndex === selected + 1));
    const fastest = r.round?.fastest?.playerId === myId;
    const gained = (r.awarded?.[myId] ?? 0);
    if (iAmCorrect) {
      sfx.correct({ fastest, big: gained >= 12 });
    } else {
      sfx.wrong();
    }
    if (gained > 0) sfx.points(gained);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.reveal]);

  // When the server ends the game, route to the game over screen to show final scores
  useEffect(() => {
    if (state && code && state.inGame === false) {
      // Small delay to let the last reveal render
      const t = setTimeout(() => navigate(`/syncduel/${code}/over`, { replace: true }), 800);
      return () => clearTimeout(t);
    }
  }, [state?.inGame, code, navigate]);

  // Progress percentage and smooth color from green -> yellow -> red
  // For background hue, derive from time left indirectly via SmoothTimer callback toggles.
  // We'll keep a stable hue base and let the timer child control the progress visuals.
  const hue = 200;

  // Goofy mascot mood based on situation
  const mascotMood = useMemo(() => {
    if (state?.reveal) {
      if (selected === null) return '🤓';
      return (state.reveal.correctIndex === selected + 1) ? '😎' : '😵';
    }
    if (urgent) return '😬';
    if (selected !== null) return '🤞';
    return '😊';
  }, [state?.reveal, selected, urgent]);

  const showConfetti = !!state?.reveal && selected !== null && (state?.reveal?.correctIndex === selected + 1);
  const confettiItems = useMemo(() => Array.from({ length: isMobile ? 14 : 24 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 0.3,
    rot: Math.random() * 60 - 30,
    emoji: ['🎉','✨','🎈','💥','⭐','🎊'][i % 6],
  })), [isMobile, state?.reveal]);

  return (
    <Box
      onPointerDown={onFirstGesture}
      color="white"
      minH="100vh"
      position="relative"
      px={{ base: 3, md: 6 }}
      py={{ base: 3, md: 4 }}
      // Dynamic playful background that slowly shifts hue based on timer
      bgGradient={`linear(to-b, #0b0b0b, #0f0f1a)`}
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
        bg: `radial-gradient(1200px 600px at 50% -10%, hsl(${(hue+40)%360}, 70%, 18%), transparent 60%), radial-gradient(900px 500px at 90% 10%, hsl(${(hue+300)%360}, 70%, 12%), transparent 60%)`,
        opacity: 0.7,
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {/* Edge progress bars (non-interactive) */}
      <EdgeBars
        leftRatio={ratioLeft}
        rightRatio={ratioRight}
        leftGained={gainedLeft > 0}
        rightGained={gainedRight > 0}
      />
  <Box position="relative" zIndex={2}>
      {/* Debug HUD: who am I and am I connected */}
      <HStack mb={2} justify="space-between" opacity={0.6} fontSize="xs">
        <Text>Socket connecté: {connected ? 'oui' : 'non'}</Text>
        <Text>Moi: {localStorage.getItem('syncduel:name') || auth.currentUser?.displayName || '—'}</Text>
        <Text>Room: {code}</Text>
      </HStack>
  {/* Header: players + timer */}
      <HStack justify="space-between" align="center" mb={{ base: 3, md: 4 }} position="relative">
        <HStack position="relative">
          <Heading size={small ? 'sm' : 'md'}><PlayerName userId={p1?.uid || p1?.id || ''} userName={p1?.name||'J1'} checkEffect={!!p1?.uid} /></Heading>
          <Badge colorScheme="yellow" variant="subtle">{p1?.score||0}</Badge>
          <AnimatePresence>
            {!!state?.reveal && (state.reveal.awarded?.[p1?.id||''] ?? 0) > 0 && (
              <MotionBox
                key="p1-burst"
                position="absolute"
                top={-5}
                left="100%"
                ml={2}
                initial={{ y: 8, opacity: 0, scale: 0.9 }}
                animate={{ y: -6, opacity: 1, scale: 1 }}
                exit={{ y: -18, opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                pointerEvents="none"
              >
                <HStack spacing={1}>
                  <Text>{state?.reveal?.round?.fastest?.playerId === p1?.id ? '⚡' : ((state?.reveal?.awarded?.[p1?.id||'']||0) <= 12 ? '😬' : '😄')}</Text>
                  <Text fontWeight="bold" color="#ffd84d">+{state?.reveal?.awarded?.[p1?.id||'']||0}</Text>
                </HStack>
              </MotionBox>
            )}
          </AnimatePresence>
        </HStack>
        <SmoothTimer
          small={!!small}
          endsAt={state?.question?.endsAt || 0}
          durationMs={((state as any)?.timePerQuestion || 10) * 1000}
          paused={!!state?.paused}
          onUrgencyChange={setUrgent}
          onSecond={(sec)=> { try { sfx.tick(sec); } catch {} }}
        />
        <HStack position="relative">
          <Badge colorScheme="cyan" variant="subtle">{p2?.score||0}</Badge>
          <Heading size={small ? 'sm' : 'md'}><PlayerName userId={p2?.uid || p2?.id || ''} userName={p2?.name||'J2'} checkEffect={!!p2?.uid} /></Heading>
          <AnimatePresence>
            {!!state?.reveal && (state.reveal.awarded?.[p2?.id||''] ?? 0) > 0 && (
              <MotionBox
                key="p2-burst"
                position="absolute"
                top={-5}
                right="100%"
                mr={2}
                initial={{ y: 8, opacity: 0, scale: 0.9 }}
                animate={{ y: -6, opacity: 1, scale: 1 }}
                exit={{ y: -18, opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                pointerEvents="none"
              >
                <HStack spacing={1}>
                  <Text fontWeight="bold" color="#7de0ff">+{state?.reveal?.awarded?.[p2?.id||'']||0}</Text>
                  <Text>{state?.reveal?.round?.fastest?.playerId === p2?.id ? '⚡' : ((state?.reveal?.awarded?.[p2?.id||'']||0) <= 12 ? '😬' : '😄')}</Text>
                </HStack>
              </MotionBox>
            )}
          </AnimatePresence>
        </HStack>
      </HStack>

      {/* Vertical score bars */}
  <Box position="relative" zIndex={2}>
  <Bar ratio={ratioLeft} color="#ffcc00" side="left" glow={gainedLeft > 0} />
  <Bar ratio={ratioRight} color="#00d0ff" side="right" glow={gainedRight > 0} />
      </Box>

      <Box maxW="1100px" mx="auto" textAlign="center">
        {/* Goofy mascot */}
        <MotionBox
          mx="auto"
          mb={{ base: 2, md: 3 }}
          w={{ base: '72px', md: '88px' }}
          h={{ base: '72px', md: '88px' }}
          borderRadius="full"
          display="grid"
          placeItems="center"
          bg="rgba(255,255,255,0.06)"
          border="1px solid rgba(255,255,255,0.12)"
          boxShadow="0 6px 20px rgba(0,0,0,0.35)"
          pointerEvents="none"
          animate={state?.reveal
            ? (showConfetti ? { scale: [1, 1.18, 1.05, 1], rotate: [0, 8, -6, 0] } : { x: [0, -6, 6, -3, 3, 0] })
            : { y: [0, -4, 0, 3, 0], rotate: [0, 1, 0, -1, 0] }}
          transition={{ duration: state?.reveal ? 0.7 : 3, repeat: state?.reveal ? 0 : Infinity, ease: 'easeInOut' }}
        >
          <Text fontSize={{ base: '3xl', md: '4xl' }}>{mascotMood}</Text>
        </MotionBox>

        {/* Question info bar: Category + Difficulty - TOUJOURS VISIBLE */}
        <Box 
          mb={3} 
          display="flex" 
          justifyContent="center" 
          alignItems="center"
          gap={3}
          flexWrap="wrap"
        >
          {/* Category badge */}
          <Badge
            px={3}
            py={1}
            borderRadius="full"
            bg="rgba(147, 51, 234, 0.3)"
            color="#c084fc"
            border="1px solid rgba(147, 51, 234, 0.5)"
            fontSize="sm"
            fontWeight="600"
          >
            {state?.question?.categoryEmoji || '📚'} {state?.question?.categoryName || 'Culture'}
          </Badge>
          {/* Difficulty stars */}
          <Box 
            display="flex" 
            alignItems="center" 
            gap={1}
            px={3}
            py={1}
            borderRadius="full"
            bg="rgba(236, 72, 153, 0.2)"
            border="1px solid rgba(236, 72, 153, 0.4)"
          >
            {[1, 2, 3, 4, 5].map((star) => (
              <Text 
                key={star}
                fontSize="sm"
                color={(state?.question?.difficulty ?? 1) >= star ? '#ec4899' : 'rgba(236, 72, 153, 0.3)'}
              >
                ★
              </Text>
            ))}
          </Box>
        </Box>

        <Heading size={small ? 'md' : 'lg'} mb={{ base: 5, md: 8 }}>{state?.question?.text || '...'}</Heading>

        {/* Affichage de l'image si présente */}
        {state?.question?.imageUrl && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mb={{ base: 4, md: 6 }}
          >
            <img
              src={state.question.imageUrl}
              alt="Question illustration"
              style={{
                maxWidth: '300px',
                maxHeight: '200px',
                borderRadius: '8px',
                objectFit: 'contain',
                border: '2px solid var(--accent-color)',
              }}
              onError={(e) => {
                console.warn('❌ [SyncDuel] Image failed to load:', state.question?.imageUrl);
                e.currentTarget.style.display = 'none';
              }}
              onLoad={() => {
                console.log('✅ [SyncDuel] Image loaded successfully:', state.question?.imageUrl);
              }}
            />
          </Box>
        )}

  <Grid templateColumns={`repeat(${cols}, minmax(0, 1fr))`} gap={{ base: 3, md: 4 }} mt={{ base: 2, md: 2 }} position="relative" zIndex={2}>
          {options.map((opt, i)=> (
            <GridItem key={i}>
              <MotionBox
                whileHover={state?.reveal || selected!==null ? {} : { y: -2, scale: 1.01 }}
                whileTap={state?.reveal || selected!==null ? {} : { scale: 0.98 }}
              >
                <Button
                  onClick={()=> handleAnswer(i)}
                  w="100%"
                  py={{ base: 7, md: 9 }}
                  borderRadius="lg"
                  bg={state?.reveal
                    ? (state.reveal.correctIndex === i+1 ? 'green.600' : (selected===i ? 'red.600' : '#111'))
                    : (selected===i ? 'purple.700' : '#111')}
                  _hover={{ bg: state?.reveal ? undefined : (selected===i ? 'purple.600' : '#151515') }}
                  border="1px solid #333"
                  whiteSpace="normal"
                  isDisabled={selected!==null || !!state?.reveal || !!state?.paused}
                  position="relative"
                  overflow="hidden"
      zIndex={3}
                  onMouseEnter={()=> { if (!state?.reveal && selected===null) try { sfx.hover(); } catch {} }}
                >
                  <HStack w="100%" justify="space-between" align="center">
                    <Badge colorScheme="purple" variant="outline" fontSize="0.7rem">{String.fromCharCode(65 + i)}</Badge>
                    <Text flex="1" mx={3} textAlign="center">{opt}</Text>
                    <Box w="20px" />
                  </HStack>

                  {/* Answer correctness tick/cross on reveal */}
                  <AnimatePresence>
                    {state?.reveal && (
                      <MotionBox
                        position="absolute"
                        top={2}
                        right={3}
                        initial={{ scale: 0, rotate: -20, opacity: 0 }}
                        animate={{ scale: 1, rotate: 0, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 16 }}
                      >
                        <Text fontSize="lg">
                          {state.reveal.correctIndex === i+1 ? '✅' : (selected===i ? '❌' : '')}
                        </Text>
                      </MotionBox>
                    )}
                  </AnimatePresence>

                  {/* Points burst now shown near player headers */}
                </Button>
              </MotionBox>
            </GridItem>
          ))}
        </Grid>

        {selected !== null && !state?.reveal && (
          <Text mt={3} color="gray.300">Réponse envoyée — en attente de l’adversaire…</Text>
        )}

        {state?.reveal && (
          <Text mt={4} color="#9cf">Bonne réponse: {state.reveal.correctIndex}</Text>
        )}
      {/* Mute toggle placed top-right, not in header, so it doesn't squeeze the timer */}
      <IconButton
        aria-label={muted ? 'Activer le son' : 'Couper le son'}
        position="absolute"
        top={{ base: 2, md: 3 }}
        right={{ base: 2, md: 3 }}
        size="sm"
        variant="ghost"
        color="gray.300"
        onClick={() => { try { sfx.toggleMute(); setMuted(sfx.isMuted()); } catch {} }}
        icon={<Box as="span" fontSize="lg">{muted ? '🔇' : '🔊'}</Box>}
      />
    </Box>
    {/* Close wrapper around header/bars/content */}
    </Box>

      {/* Block clicks during reveal or pause */}
      {(state?.reveal || state?.paused) && (
        <Box position="absolute" inset={0} zIndex={1} pointerEvents="auto" bg="transparent" />
      )}

      {/* Pause overlay with countdown */}
      {state?.paused && (
        <Box position="absolute" inset={0} display="grid" placeItems="center" zIndex={5} bg="rgba(0,0,0,0.6)">
          <VStack spacing={3}>
            <Heading size={small ? 'md' : 'lg'}>Partie en pause</Heading>
            <Text>
              {(() => {
                const dc = (state?.players || []).find(p => p.connected === false);
                const pseudo = dc?.name || 'un joueur';
                return `En attente de la reconnexion de ${pseudo}…`;
              })()}
            </Text>
            {state?.pauseDeadline && (
              <PauseCountdown until={state.pauseDeadline} />
            )}
          </VStack>
        </Box>
      )}

      {/* Lightweight emoji confetti when you got it right */}
      <AnimatePresence>
        {showConfetti && (
          <Box position="absolute" inset={0} pointerEvents="none" overflow="hidden" zIndex={1}>
            {confettiItems.map((c) => (
              <MotionBox
                key={c.id}
                position="absolute"
                top="-10%"
                left={`${c.x}%`}
                initial={{ y: '-10%', opacity: 0, rotate: c.rot }}
                animate={{ y: '110%', opacity: [0,1,1,0.8], rotate: c.rot + 360 }}
                transition={{ duration: 1.4 + Math.random()*0.4, delay: c.delay, ease: 'easeOut' }}
              >
                <Text fontSize={isMobile ? 'md' : 'lg'}>{c.emoji}</Text>
              </MotionBox>
            ))}
          </Box>
        )}
      </AnimatePresence>
    </Box>
  );
}

// Local countdown badge for pause overlay
function PauseCountdown({ until }: { until: number }) {
  const [left, setLeft] = useState<number>(() => Math.max(0, Math.ceil((until - Date.now()) / 1000)));
  useEffect(() => {
    const id = setInterval(() => setLeft(Math.max(0, Math.ceil((until - Date.now()) / 1000))), 500);
    return () => clearInterval(id);
  }, [until]);
  return <Badge colorScheme="orange" fontSize="md">{left}s</Badge>;
}
